import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { UtilityService } from "@core/utility.service";
import { TranslateLoader } from "@ngx-translate/core";
import { doc, getDoc, getFirestore, loadBundle } from "firebase/firestore";
import * as JSZip from "jszip";
import { BehaviorSubject, firstValueFrom, from, Observable, of } from "rxjs";
import { catchError, filter, shareReplay, switchMap, take, tap, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable } from "../app.constants";
import { LoggingService } from "./logging.service";
import { AuthService } from "@core/auth.service";

@Injectable({
  providedIn: "root",
})
export class FirestoreTransLoader implements TranslateLoader {
  private utilityService = inject(UtilityService);
  protected loggingService = inject(LoggingService);
  private authService = inject(AuthService);
  private validLanguages: Set<string> = new Set();
  private languageListLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {
    this.utilityService
      .getLanguageList()
      .pipe(take(1))
      .subscribe({
        next: (l: Data) => {
          if (!l?.languages?.length) return;
          this.validLanguages = new Set(
            l.languages.filter((lang) => lang.language_status).map((lang) => lang.language_code)
          );
          this.languageListLoaded$.next(true);
        },
        error: (error) => {
          console.error("[Language List] Error:", error);
          this.languageListLoaded$.next(true);
        },
      });
  }

  getTranslation(lang: any): Observable<any> {
    let selectedLang = typeof lang === "object" ? lang["language_code"] : lang;
    selectedLang = selectedLang?.includes("lan") ? "en" : selectedLang || "en";

    return this.languageListLoaded$.pipe(
      filter((loaded) => loaded),
      take(1),
      switchMap(() => {
        if (this.validLanguages.size && !this.validLanguages.has(selectedLang)) {
          this.authService.errorHandlerLogs(`[getTranslation] Invalid language "${selectedLang}", defaulting to "en"`);
          selectedLang = "en";
        }

        const existingTranslation = CommonVariable.translation$?.value;

        if (existingTranslation?.lang === selectedLang && Object.keys(existingTranslation?.data || {}).length) {
          return of(existingTranslation.data);
        }
        return this.returnTranslatedData(selectedLang);
      }),
      catchError((err) => {
        console.error(`[getTranslation] Critical error loading translations for "${selectedLang}":`, err);

        // Emergency fallback - try to load static file directly
        return from(this.getStaticTranslationFallback(selectedLang)).pipe(
          tap(() => console.warn(`[getTranslation] Using static fallback translations for "${selectedLang}"`)),
          catchError(() => {
            // Ultimate fallback - return empty object to prevent complete app failure
            console.error(`[getTranslation] All translation loading methods failed for "${selectedLang}"`);
            return of({});
          })
        );
      })
    );
  }

  private async getStaticTranslationFallback(lang: string): Promise<Data | null> {
    try {
      console.warn(`[getStaticTranslationFallback] Loading static translation file for "${lang}"`);

      // Check if the language file exists in our static assets
      const staticFiles = {
        de: "/assets/i18n/de.json",
        en: "/assets/i18n/en.json",
        es: "/assets/i18n/es.json",
        fr: "/assets/i18n/fr.json",
        it: "/assets/i18n/it.json",
      };

      const filePath = staticFiles[lang] || staticFiles["en"]; // Default to English if language not found

      const translationData = await firstValueFrom(this.http.get<Data>(filePath, { responseType: "json" }));

      return translationData || {};
    } catch (error) {
      this.loggingService.logError(
        `[getStaticTranslationFallback] Error loading static fallback for "${lang}":`,
        error
      );
      return {};
    }
  }

  private async fetchTranslatedData(lang: string, retryCase = false): Promise<Data> {
    let bundleText: string | null = null;

    try {
      // 1. Try primary CDN with timeout
      const cdnUrl = environment.translationBundleCDNUrl?.[lang];
      if (cdnUrl) {
        try {
          const response = await firstValueFrom(
            this.http
              .get(cdnUrl, {
                responseType: "text",
                headers: new HttpHeaders({
                  "Cache-Control": "no-cache",
                }),
              })
              .pipe(
                timeout(8000),
                catchError((err) => {
                  if (err.name === "TimeoutError") {
                    console.warn(`[fetchTranslatedData] CDN request timeout for "${lang}"`);
                  } else {
                    console.warn(`[fetchTranslatedData] CDN request failed for "${lang}":`, err);
                  }
                  return of(null);
                })
              )
          );
          if (response) {
            bundleText = response;
          } else {
            console.warn(`[fetchTranslatedData] CDN response empty or invalid for "${lang}"`);
          }
        } catch (cdnError: any) {
          if (cdnError.name === "AbortError") {
            console.warn(`[fetchTranslatedData] CDN request timeout for "${lang}"`);
          } else {
            console.warn(`[fetchTranslatedData] CDN request failed for "${lang}":`, cdnError);
          }
        }
      }

      // 2. If CDN failed, try remote bundle
      if (!bundleText) {
        try {
          bundleText = await this.getRemoteBundle(lang);
        } catch (remoteError) {
          this.loggingService.logError(`[fetchTranslatedData] Remote bundle error for "${lang}":`, remoteError);
          bundleText = null;
        }
      }

      // 3. If we have bundle text, load into Firestore cache
      if (bundleText) {
        try {
          const db = getFirestore();
          await loadBundle(db, bundleText);

          const ref = doc(db, "translates", lang);
          const snapshot = await getDoc(ref);
          const data = snapshot.exists() ? (snapshot.data() as Data) : null;

          if (data && Object.keys(data).length > 0) {
            return data;
          } else {
            console.warn(`[fetchTranslatedData] Bundle loaded but no valid data found for "${lang}"`);
          }
        } catch (bundleError) {
          this.loggingService.logError(`[fetchTranslatedData] Bundle loading error for "${lang}":`, bundleError);
        }
      }

      // 4. If retry is allowed, try once more
      if (!retryCase) {
        console.warn(`[fetchTranslatedData] Retrying fetch for "${lang}" (second attempt)...`);
        return this.fetchTranslatedData(lang, true);
      }

      // 5. Firestore direct fallback
      try {
        const db = getFirestore();
        const ref = doc(db, "translates", lang);
        const snapshot = await getDoc(ref);
        const data = snapshot.exists() ? (snapshot.data() as Data) : null;

        if (data && Object.keys(data).length > 0) {
          return data;
        }
      } catch (fallbackError) {
        this.loggingService.logError(
          `[fetchTranslatedData] Firestore direct fallback error for "${lang}":`,
          fallbackError
        );
      }

      // 6. Final static JSON fallback
      console.warn(`[fetchTranslatedData] All sources failed, falling back to static JSON for "${lang}"`);
      return this.getStaticTranslationFallback(lang);
    } catch (error) {
      console.warn(`[fetchTranslatedData] Returning ultimate fallback for "${lang}"`);
      return this.getStaticTranslationFallback(lang);
    }
  }

  private returnTranslatedData(lang: string): Observable<Data> {
    return from(this.fetchTranslatedData(lang)).pipe(
      switchMap((translatedData) => {
        if (translatedData) {
          CommonVariable.translation$.next({ lang, data: translatedData });
          return of(translatedData);
        }
        console.warn(`[returnTranslatedData] No data found for "${lang}"`);
        return of(null);
      }),
      catchError((err) => {
        console.error(`[returnTranslatedData] Error for "${lang}":`, err);
        return of(null);
      }),
      shareReplay(1)
    );
  }

  private async getLocalBundle(lang: string): Promise<string | null> {
    const zipPaths = {
      de: "../../assets/bundles_translates_de.zip",
      en: "../../assets/bundles_translates_en.zip",
      es: "../../assets/bundles_translates_es.zip",
      fr: "../../assets/bundles_translates_fr.zip",
      it: "../../assets/bundles_translates_it.zip",
      hi: "../../assets/bundles_translates_hi.zip",
      pt: "../../assets/bundles_translates_pt.zip",
    };
    return this.extractZipData(zipPaths?.[lang], lang);
  }

  private async extractZipData(zipUrl: string, lang: string): Promise<string | null> {
    try {
      if (!zipUrl) {
        this.loggingService.logError(`[extractZipData] No ZIP URL provided for "${lang}"`);
        return null;
      }

      const arrayBuffer = await firstValueFrom(
        this.http.get(zipUrl, { responseType: "arraybuffer" }).pipe(
          timeout(10000),
          catchError((err) => {
            console.warn(`[extractZipData] Failed to download ZIP for "${lang}":`, err);
            return of(null);
          })
        )
      );

      if (!arrayBuffer) {
        console.warn(`[extractZipData] ZIP not found at "${zipUrl}". Using fallback.`);
        return this.getLocalBundleFallback(lang);
      }

      const zip = await JSZip.loadAsync(arrayBuffer);
      const bundleFile = zip.file("bundleData.txt");

      if (!bundleFile) {
        this.loggingService.logError(`[extractZipData] bundleData.txt not found in ZIP for "${lang}"`);
        return null;
      }

      const textFile = await bundleFile.async("string");
      return textFile || null;
    } catch (err) {
      this.loggingService.logError(`[extractZipData] Unexpected error for "${lang}":`, err);
      return null;
    }
  }

  private async getRemoteBundle(lang: string, retryCase = false): Promise<string | null> {
    const zipUrl = environment.translationBundleZipUrl as any;
    try {
      const bundleText = await this.extractZipData(zipUrl, lang);
      if (!bundleText && !retryCase) {
        await this.delay(1000);
        return this.getRemoteBundle(lang, true);
      }
      return bundleText ?? (await this.getLocalBundleFallback(lang));
    } catch (err) {
      console.error(`[getRemoteBundle] Error for "${lang}":`, err);
      if (!retryCase) {
        await this.delay(1000);
        return this.getRemoteBundle(lang, true);
      }
      return this.getLocalBundleFallback(lang);
    }
  }

  private async getLocalBundleFallback(lang: string): Promise<string | null> {
    try {
      console.warn(`[getLocalBundleFallback] Falling back to local ZIP for "${lang}"`);
      return await this.getLocalBundle(lang);
    } catch (err) {
      this.loggingService.logError(`[getLocalBundleFallback] Error loading local bundle for "${lang}":`, err);
      return null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
